import {Flag, Tooltip} from 'platform/components';
import {HStack} from 'platform/foundation';

import {isNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {CellRendererProps} from '../../types/CellRendererProps';
import {TooltipGetter} from '../../types/TooltipGetter';
import {getTooltipProps} from '../../utils/getTooltipProps';
import {EmptyCellValue} from '../EmptyCellValue/EmptyCellValue';
import {getFlagTooltipLabel} from './utils/getFlagTooltipLabel';

// Whole raw API cell shape for ENUM+FLAG columns (getValueFromRawCell returns this unchanged)
type FlagItem = {label: string; key?: string};

type FlagStyle = {flagColor?: string; flagSubtle?: boolean};

type FlagRawCell = {
  value: FlagItem | FlagItem[] | null;
  style?: FlagStyle | FlagStyle[];
  id?: string;
} | null;

type FlagCellRendererProps = CellRendererProps<FlagRawCell> & {
  getTooltipProps?: TooltipGetter;
};

export function FlagCellRenderer(props: FlagCellRendererProps) {
  const tooltipProps = getTooltipProps(props.row, props.getTooltipProps);

  if (isNil(props.value)) {
    return <EmptyCellValue emptyCellValue={props.emptyCellValue} isWrap={props.isWrap} />;
  }

  const raw = props.value;
  const rawStyle = raw?.style;
  const cellValue = raw?.value;

  if (isNil(cellValue) || (isArray(cellValue) && cellValue.length === 0)) {
    return <EmptyCellValue emptyCellValue={props.emptyCellValue} isWrap={props.isWrap} />;
  }

  if (!isArray(cellValue)) {
    const style = isArray(rawStyle) ? rawStyle[0] : rawStyle;
    return (
      <Tooltip {...tooltipProps}>
        <Flag
          colorScheme={style?.flagColor as never}
          isSubtle={style?.flagSubtle ?? false}
          label={cellValue.label}
        />
      </Tooltip>
    );
  }

  return (
    <HStack spacing={1} wrap={props.isWrap}>
      {cellValue.map((item, index) => {
        if (isNil(item)) {
          return null;
        }
        const itemStyle = isArray(rawStyle) ? rawStyle[index] : rawStyle;
        return (
          <Tooltip
            key={`${item.label}-${index}`} // eslint-disable-line react/no-array-index-key -- Pre-existing violation
            label={getFlagTooltipLabel(cellValue, item.label)}
            isDisabled={tooltipProps.isDisabled}
            openDelay={tooltipProps.openDelay}
            hasAutoWidth
          >
            <Flag
              colorScheme={itemStyle?.flagColor as never}
              isSubtle={itemStyle?.flagSubtle ?? false}
              label={item.label}
            />
          </Tooltip>
        );
      })}
    </HStack>
  );
}
